import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Clock,
  Eye,
  MessagesSquare,
  ShieldCheck,
  Sparkles,
  UserCog,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { formatDate, timeAgo } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import {
  AxisBreakdown,
  FeedbackDrawer,
  PeriodSelector,
  TrendSparkline,
  deriveAggregateBadge,
  formatPeriodLabel,
  formatScore,
  lastNPeriodKeys,
  type FeedbackAvailableResponse,
  type FeedbackAggregateResponse,
  type FeedbackSlot,
  type FeedbackTrendResponse,
} from '../components/feedback';

export const Route = createFileRoute('/_authed/me/feedback')({
  component: MyFeedbackPage,
});

function MyFeedbackPage() {
  const role = useAuthStore((s) => s.user?.primaryRole);

  if (role === 'teacher') {
    return <TeacherFeedbackView />;
  }
  if (role === 'parent') {
    return <ParentFeedbackView />;
  }
  return (
    <ListShell
      title="Feedback"
      description="This screen is available to parents and teachers."
    >
      <EmptyState
        title="No feedback for your role"
        description="Use your dashboard for your role."
        icon={<MessagesSquare size={32} aria-hidden="true" />}
        action={
          <Link to="/dashboard">
            <Button variant="secondary">Back to dashboard</Button>
          </Link>
        }
      />
    </ListShell>
  );
}

// =========================================================================
// Parent view
// =========================================================================

function ParentFeedbackView() {
  const queryClient = useQueryClient();
  const [activeSlot, setActiveSlot] = useState<FeedbackSlot | null>(null);

  const availableQuery = useQuery({
    queryKey: ['feedback', 'available'] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAvailableResponse>('/api/me/feedback/available', {
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const data = availableQuery.data;
  const window = data?.window ?? null;
  const nextWindow = data?.nextWindow ?? null;
  const form = data?.form ?? null;
  const items = useMemo(() => data?.items ?? [], [data]);

  const heading = useMemo(() => {
    if (window) return `Monthly feedback for ${formatPeriodLabel(periodOfWindow(window.periodKey))}`;
    if (nextWindow)
      return `Feedback window opens on ${formatDate(nextWindow.opensAt)}`;
    return 'Monthly feedback';
  }, [window, nextWindow]);

  const description = useMemo(() => {
    if (window) {
      return `Your responses are anonymous to teachers. The window closes on ${formatDate(window.closesAt, { withTime: true })}.`;
    }
    if (nextWindow) {
      return `Feedback for ${formatPeriodLabel(nextWindow.periodKey)} can be submitted between ${formatDate(nextWindow.opensAt)} and ${formatDate(nextWindow.closesAt)}.`;
    }
    return 'A new feedback window opens on the 1st of every month.';
  }, [window, nextWindow]);

  const handleSaved = () => {
    setActiveSlot(null);
    void queryClient.invalidateQueries({ queryKey: ['feedback', 'available'] });
  };

  return (
    <ListShell title={heading} description={description}>
      <GatedSection
        error={availableQuery.error}
        title="Parent feedback"
        description="Once the feedback module is enabled by the academy, you'll see this month's pending feedback cards here."
        onRetry={() => availableQuery.refetch()}
      >
        {availableQuery.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !window ? (
          <ParentWindowClosed nextWindow={nextWindow} />
        ) : items.length === 0 ? (
          <EmptyState
            title={`All caught up for ${formatPeriodLabel(periodOfWindow(window.periodKey))}.`}
            description="Your feedback for the current window has been recorded. Thank you!"
            icon={<Sparkles size={32} aria-hidden="true" />}
          />
        ) : (
          <ParentSlotList
            items={items}
            onOpen={(slot) => setActiveSlot(slot)}
            window={window}
          />
        )}
      </GatedSection>

      <FeedbackDrawer
        slot={activeSlot}
        form={form}
        periodKey={window?.periodKey ?? null}
        onClose={() => setActiveSlot(null)}
        onSaved={handleSaved}
      />
    </ListShell>
  );
}

/**
 * The backend returns `periodKey` as the month being rated (i.e. the
 * previous calendar month). That's the same string we want to label —
 * this helper is a no-op today, kept for symmetry should the API ever
 * shift conventions.
 */
const periodOfWindow = (periodKey: string): string => periodKey;

interface ParentWindowClosedProps {
  nextWindow: { periodKey: string; opensAt: string; closesAt: string } | null;
}

function ParentWindowClosed({ nextWindow }: ParentWindowClosedProps) {
  return (
    <section className="relative overflow-hidden rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-6 py-10 text-center">
      <div className="mx-auto max-w-md flex flex-col items-center">
        <div className="rounded-full bg-gold-50 p-3 text-gold-700 dark:bg-gold-700/20 dark:text-gold-100">
          <CalendarClock size={28} aria-hidden="true" />
        </div>
        <h2 className="mt-4 font-display text-xl font-semibold">
          The feedback window is currently closed
        </h2>
        <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
          Feedback windows open on the 1st of every month and remain open
          for 10 days, asking about the month that just ended.
        </p>
        {nextWindow ? (
          <div className="mt-5 grid w-full gap-2 sm:grid-cols-2">
            <Stat
              label="Next window"
              value={formatPeriodLabel(nextWindow.periodKey)}
            />
            <Stat label="Opens on" value={formatDate(nextWindow.opensAt)} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

interface ParentSlotListProps {
  items: ReadonlyArray<FeedbackSlot>;
  onOpen: (slot: FeedbackSlot) => void;
  window: { periodKey: string; opensAt: string; closesAt: string };
}

function ParentSlotList({ items, onOpen, window }: ParentSlotListProps) {
  const pending = items.filter((it) => it.status === 'pending');
  const submitted = items.filter((it) => it.status !== 'pending');

  return (
    <div className="space-y-6">
      <section
        className="rounded-xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] px-5 py-4 dark:from-gold-700/20"
        aria-labelledby="parent-feedback-intro"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck
            className="mt-0.5 text-gold-700 dark:text-gold-100"
            size={20}
            aria-hidden="true"
          />
          <div>
            <p
              id="parent-feedback-intro"
              className="font-display text-sm font-semibold"
            >
              Your name stays private
            </p>
            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
              Teachers see only an aggregated score after at least 5 parents
              respond — never individual ratings or your identity. Comments
              are reviewed for safety before sharing.
            </p>
            <p className="mt-1.5 text-xs text-[color:var(--color-text-muted)]">
              Window closes{' '}
              <span className="font-medium text-[color:var(--color-text)]">
                {formatDate(window.closesAt, { withTime: true })}
              </span>
              .
            </p>
          </div>
        </div>
      </section>

      {pending.length > 0 ? (
        <section>
          <SectionHeader
            title="Pending feedback"
            count={pending.length}
            hint="Tap a card to share your monthly rating."
          />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pending.map((slot) => (
              <SlotCard
                key={`${slot.student.id}-${slot.teacher.id}`}
                slot={slot}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      ) : null}

      {submitted.length > 0 ? (
        <section>
          <SectionHeader
            title="Submitted this month"
            count={submitted.length}
            hint="Editable within the window; locked after."
          />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {submitted.map((slot) => (
              <SlotCard
                key={`${slot.student.id}-${slot.teacher.id}-${slot.submissionId ?? 'x'}`}
                slot={slot}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function SectionHeader({
  title,
  count,
  hint,
}: {
  title: string;
  count: number;
  hint?: string;
}) {
  return (
    <header className="mb-2 flex items-baseline justify-between">
      <h2 className="font-display text-base font-semibold">
        {title}
        <span className="ml-2 rounded-full bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]">
          {count}
        </span>
      </h2>
      {hint ? (
        <p className="text-xs text-[color:var(--color-text-muted)]">{hint}</p>
      ) : null}
    </header>
  );
}

interface SlotCardProps {
  slot: FeedbackSlot;
  onOpen: (slot: FeedbackSlot) => void;
}

function SlotCard({ slot, onOpen }: SlotCardProps) {
  const isPending = slot.status === 'pending';
  const isEditable = slot.status === 'editable';
  const isLocked = slot.status === 'locked';
  const isWithdrawn = slot.status === 'withdrawn';

  const hoursLeft = useMemo(() => {
    if (slot.status === 'editable') {
      const ms = new Date(slot.editableUntil).getTime() - Date.now();
      if (ms <= 0) return null;
      return Math.max(1, Math.round(ms / 3_600_000));
    }
    return null;
  }, [slot]);

  return (
    <article
      className={clsx(
        'group relative flex h-full flex-col rounded-xl border bg-[color:var(--color-bg-elevated)] p-4 transition-colors',
        'focus-within:border-gold-500 hover:border-gold-300',
        isPending && 'border-gold-300/60 bg-gradient-to-br from-gold-50/40 to-[color:var(--color-bg-elevated)] dark:from-gold-700/10',
        !isPending && 'border-[color:var(--color-border)]',
      )}
    >
      <header className="flex flex-col gap-0.5">
        <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
          For {slot.student.name}
        </p>
        <p className="font-display text-base font-semibold leading-tight">
          {slot.teacher.name}
        </p>
        <p className="text-xs text-[color:var(--color-text-muted)]">
          {slot.batch.name}
        </p>
      </header>

      <div className="mt-3 flex flex-1 items-end justify-between gap-2">
        <SlotStatusBadge
          status={slot.status}
          hoursLeft={hoursLeft}
          submittedAt={
            slot.status !== 'pending' ? slot.submittedAt : null
          }
        />
        <Button
          size="sm"
          variant={isPending ? 'primary' : 'secondary'}
          onClick={() => onOpen(slot)}
          aria-label={
            isPending
              ? `Submit feedback for ${slot.teacher.name}`
              : isEditable
                ? `Edit feedback for ${slot.teacher.name}`
                : `View feedback for ${slot.teacher.name}`
          }
        >
          {isPending
            ? 'Submit feedback'
            : isEditable
              ? 'Edit'
              : isLocked || isWithdrawn
                ? 'View'
                : 'Open'}
        </Button>
      </div>
    </article>
  );
}

function SlotStatusBadge({
  status,
  hoursLeft,
  submittedAt,
}: {
  status: FeedbackSlot['status'];
  hoursLeft: number | null;
  submittedAt: string | null;
}) {
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-gold-300 bg-gold-50 px-2 py-0.5 text-xs font-medium text-gold-900 dark:bg-gold-700/20 dark:text-gold-50">
        Pending
      </span>
    );
  }
  if (status === 'editable') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-success)]">
        <Clock size={12} aria-hidden="true" />
        Submitted · {hoursLeft ? `${hoursLeft}h to edit` : 'editable'}
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-text-muted)]">
        Submitted · locked
        {submittedAt ? <span className="ml-1">({timeAgo(submittedAt)})</span> : null}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-warning)]">
      Withdrawn
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </p>
      <p className="mt-0.5 font-display text-sm font-bold text-[color:var(--color-text)]">
        {value}
      </p>
    </div>
  );
}

// =========================================================================
// Teacher view
// =========================================================================

interface _TeacherUserShape {
  id: string;
  primaryRole: string;
}

function TeacherFeedbackView() {
  const user = useAuthStore((s) => s.user);

  // teacherId is resolved server-side on /api/me and surfaced on the auth
  // store (added in the same commit as this view's go-live). A null value
  // means the user has the teacher role but no linked teachers row yet —
  // surface a contextual empty state rather than calling the aggregate
  // endpoint blindly.
  const teacherId = user?.teacherId ?? null;
  const teacherName = user?.displayName ?? 'Teacher';
  const teacherProfileMissing = Boolean(user) && !teacherId;

  const [periodKey, setPeriodKey] = useState<string>(
    () => lastNPeriodKeys(6)[0] ?? '',
  );

  const aggregateQuery = useQuery({
    queryKey: ['feedback', 'aggregate', teacherId, periodKey] as const,
    enabled: Boolean(teacherId) && Boolean(periodKey),
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAggregateResponse>(
        `/api/feedback/teachers/${encodeURIComponent(teacherId!)}/aggregate`,
        { signal, query: { periodKey } },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const trendQuery = useQuery({
    queryKey: ['feedback', 'trend', teacherId] as const,
    enabled: Boolean(teacherId),
    queryFn: ({ signal }) =>
      apiRequest<FeedbackTrendResponse>(
        `/api/feedback/teachers/${encodeURIComponent(teacherId!)}/trend`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // Surface the *aggregate* call's error to GatedSection so a disabled
  // `module_feedback` flag still renders the canonical gated card. The
  // `/api/me/teacher-profile` 404 is handled separately so the teacher
  // sees a self-serve hint rather than a "module disabled" panel.
  const primaryError = aggregateQuery.error;

  return (
    <ListShell
      title="My feedback"
      description={`Anonymous monthly feedback for ${teacherName}. Visible only to you once at least 5 parents have responded.`}
    >
      <GatedSection
        error={primaryError}
        title="My feedback"
        description="Once the feedback module is enabled by the academy, you'll see your monthly aggregate here."
        onRetry={() => {
          void aggregateQuery.refetch();
          void trendQuery.refetch();
        }}
      >
        {teacherProfileMissing ? (
          <EmptyState
            title="Teacher profile not linked yet"
            description="Your account is marked as a teacher but is not yet linked to a teacher record on this server. Please contact the academy office so your monthly feedback aggregate can be shown here."
            icon={<UserCog size={32} aria-hidden="true" />}
          />
        ) : !teacherId ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <TeacherAggregateView
            teacherName={teacherName}
            periodKey={periodKey}
            onPeriodChange={setPeriodKey}
            aggregateData={aggregateQuery.data}
            isAggregateLoading={aggregateQuery.isLoading}
            trendData={trendQuery.data}
            isTrendLoading={trendQuery.isLoading}
          />
        )}
      </GatedSection>
    </ListShell>
  );
}

interface TeacherAggregateViewProps {
  teacherName: string;
  periodKey: string;
  onPeriodChange: (next: string) => void;
  aggregateData: FeedbackAggregateResponse | undefined;
  isAggregateLoading: boolean;
  trendData: FeedbackTrendResponse | undefined;
  isTrendLoading: boolean;
}

function TeacherAggregateView({
  periodKey,
  onPeriodChange,
  aggregateData,
  isAggregateLoading,
  trendData,
  isTrendLoading,
}: TeacherAggregateViewProps) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[200px]">
          <PeriodSelector value={periodKey} onChange={onPeriodChange} />
        </div>
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Showing aggregate for {formatPeriodLabel(periodKey)}
        </p>
      </div>

      {isAggregateLoading || !aggregateData ? (
        <Skeleton className="h-64 w-full" />
      ) : aggregateData.insufficient ? (
        <TeacherInsufficient
          responsesCount={aggregateData.responsesCount}
          minVisibleResponseCount={aggregateData.minVisibleResponseCount}
          periodKey={periodKey}
        />
      ) : (
        <TeacherSufficient data={aggregateData} />
      )}

      <section>
        <header className="mb-2 flex items-baseline justify-between">
          <h2 className="font-display text-base font-semibold">
            Last 6 months
          </h2>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Periods below the response threshold appear as faded markers.
          </p>
        </header>
        <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
          {isTrendLoading || !trendData ? (
            <Skeleton className="h-32 w-full" />
          ) : trendData.items.length === 0 ? (
            <p className="text-sm text-[color:var(--color-text-muted)]">
              No trend yet. The first month with at least 5 responses will
              appear here.
            </p>
          ) : (
            <TrendSparkline items={trendData.items} />
          )}
        </div>
      </section>
    </div>
  );
}

function TeacherInsufficient({
  responsesCount,
  minVisibleResponseCount,
  periodKey,
}: {
  responsesCount: number;
  minVisibleResponseCount: number;
  periodKey: string;
}) {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-6 py-10 text-center">
      <div className="mx-auto max-w-md flex flex-col items-center">
        <div className="rounded-full bg-gold-50 p-3 text-gold-700 dark:bg-gold-700/20 dark:text-gold-100">
          <ShieldCheck size={28} aria-hidden="true" />
        </div>
        <h2 className="mt-4 font-display text-xl font-semibold">
          Not enough responses yet for {formatPeriodLabel(periodKey)}
        </h2>
        <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
          Aggregated scores are hidden until at least{' '}
          <span className="font-medium text-[color:var(--color-text)]">
            {minVisibleResponseCount}
          </span>{' '}
          parents respond, to protect every parent's anonymity. We currently
          have{' '}
          <span className="font-medium text-[color:var(--color-text)]">
            {responsesCount}
          </span>{' '}
          {responsesCount === 1 ? 'response' : 'responses'}.
        </p>
      </div>
    </section>
  );
}

function TeacherSufficient({
  data,
}: {
  data: Extract<FeedbackAggregateResponse, { insufficient: false }>;
}) {
  const aggregate = data.aggregate;
  const submissions = data.submissions ?? [];
  const visibleSubmissions = submissions.filter(
    (s) =>
      s.moderationStatus !== 'hidden' &&
      typeof s.comment === 'string' &&
      s.comment.trim().length > 0,
  );

  const badge = aggregate
    ? deriveAggregateBadge(
        aggregate.responsesCount,
        aggregate.overallAvgX100,
      )
    : null;

  return (
    <div className="space-y-6">
      <section
        className="rounded-xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-5 dark:from-gold-700/20"
        aria-label="Feedback summary"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
              {formatPeriodLabel(data.periodKey)}
            </p>
            <div className="mt-1 flex items-baseline gap-3">
              <p className="font-display text-4xl font-bold tabular-nums">
                {aggregate ? formatScore(aggregate.overallAvgX100) : '—'}
              </p>
              <span className="text-sm text-[color:var(--color-text-muted)]">
                / 5 overall
              </span>
              {badge ? (
                <span
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                    badge.tone === 'gold'
                      ? 'border-gold-500 bg-gold-50 text-gold-900 dark:bg-gold-700/20 dark:text-gold-50'
                      : 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]',
                  )}
                >
                  {badge.label}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
              From{' '}
              <span className="font-medium text-[color:var(--color-text)]">
                {aggregate?.responsesCount ?? 0}
              </span>{' '}
              parent responses.
            </p>
          </div>
        </div>
      </section>

      {aggregate ? (
        <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
          <header className="mb-3">
            <h3 className="font-display text-base font-semibold">
              How parents rated each area
            </h3>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              Averages across the five questions in this period.
            </p>
          </header>
          <AxisBreakdown aggregate={aggregate} />
        </section>
      ) : null}

      <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <header className="mb-3 flex items-start gap-2">
          <Eye
            className="mt-0.5 text-[color:var(--color-text-muted)]"
            size={16}
            aria-hidden="true"
          />
          <div>
            <h3 className="font-display text-base font-semibold">
              Anonymous comments
            </h3>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              These comments are shown to you only. Parent identities are
              hidden.
            </p>
          </div>
        </header>
        <div aria-live="polite">
          {visibleSubmissions.length === 0 ? (
            <p className="text-sm text-[color:var(--color-text-muted)]">
              No comments were left for this period.
            </p>
          ) : (
            <ul className="space-y-3">
              {visibleSubmissions.slice(0, 20).map((s) => (
                <li
                  key={s.id}
                  className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2"
                >
                  <p className="text-sm text-[color:var(--color-text)] whitespace-pre-wrap">
                    “{s.comment}”
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
                    {timeAgo(s.submittedAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

